import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
    Box, Typography, Card, CardContent, CardActionArea,
    Button, IconButton, Chip, Alert,
    TextField, Paper, Stack, Avatar, Divider, Tooltip, Collapse,
    Switch, FormControlLabel, CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import TvIcon from '@mui/icons-material/Tv';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { fetchScreens, createScreen, updateScreen, deleteScreen, fetchPlaylists, fetchPlaylistsByScreen, updatePlaylist, createPlaylist, deletePlaylist, createDashboardInPlaylist, deleteDashboardFromPlaylist, fetchDashboard, fetchDashboards } from './actions/screenActions';
import { selectEffectiveLimit } from '../../../store/actions/licenseActions';
import { isUnlimited, getSuspendedScreenIds } from '../../../utils/licenseUtils';
import { ConfigDialog } from '../../../dummies/ConfigDialog/ConfigDialog';
import { CenterDialog } from '../../../dummies/CenterDialog/CenterDialog';
import LoadingOverlay from '../../../dummies/LoadingOverlay/LoadingOverlay';
import ScheduleCalendarDialog from '../../../dummies/ScheduleCalendar/ScheduleCalendarDialog';
import PlaylistScheduleIndicator from '../../../dummies/PlaylistScheduleIndicator/PlaylistScheduleIndicator';
import DashboardTemplateSelector from '../../../dummies/DashboardTemplateSelector/DashboardTemplateSelector';
import Toast from '../../../dummies/Toast/Toast';
import SettingsIcon from '@mui/icons-material/Settings';
import Slider from '@mui/material/Slider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

/**
 * Screen - ניהול מסכים
 * מציג רשימת מסכים או פרטי מסך בודד
 */
const Screen = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { organizationId, screenId } = useParams();
    const organization = useSelector(state => state.organization);
    const catalog = useSelector(state => state.app?.catalog || []);
    const { screens, dashboards, loading: screensLoading, error: screensError } = useSelector(state => state.screens);
    const screensLimit = useSelector(state => selectEffectiveLimit(state, 'screens'));
    const playlistsPerScreenLimit = useSelector(state => selectEffectiveLimit(state, 'playlistsPerScreen'));
    const dashboardsPerPlaylistLimit = useSelector(state => selectEffectiveLimit(state, 'dashboardsPerPlaylist'));
    const suspendedScreenIds = getSuspendedScreenIds(screens, screensLimit);
    const { predefinedTemplates } = useSelector(state => state.app);

    // שליפת כל הפלייליסטים מ-Redux
    const allPlaylists = useSelector(state => state.screens.playlists);

    // סינון dashboards פעילים בלבד — רק כאלה שמשויכים ל-playlist כלשהו
    const activeDashboards = useMemo(() => {
        const activeIds = new Set();
        (allPlaylists || []).forEach(playlist => {
            (playlist.items || []).forEach(item => {
                if (item.dashboardId) activeIds.add(item.dashboardId);
            });
        });
        return (dashboards || []).filter(d => activeIds.has(d.dashboardId));
    }, [dashboards, allPlaylists]);

    // סינון מקומי עם useMemo - אם אנחנו בתצוגת פירוט, נציג רק פלייליסטים ששייכים למסך הזה
    const isDetailView = !!screenId;
    const playlists = useMemo(() => {
        return isDetailView
            ? allPlaylists.filter(p => p.screenId === screenId)
            : allPlaylists;
    }, [allPlaylists, screenId, isDetailView]);

    // מציאת המסך הנוכחי מתוך המערך לפי screenId מה-params
    const currentScreen = screenId ? screens.find(s => s.screenId === screenId) : null;

    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [newScreenName, setNewScreenName] = useState('');
    const [newScreenDescription, setNewScreenDescription] = useState('');
    const [newScreenUrl, setNewScreenUrl] = useState('');
    const [creating, setCreating] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);
    const [urlError, setUrlError] = useState('');

    // Template selector for new screen creation
    const [newScreenTemplateSelectorOpen, setNewScreenTemplateSelectorOpen] = useState(false);
    const [newScreenSelectedTemplateId, setNewScreenSelectedTemplateId] = useState(null);

    // For edit dialog
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingScreen, setEditingScreen] = useState(null);
    const [editScreenName, setEditScreenName] = useState('');
    const [editScreenDescription, setEditScreenDescription] = useState('');
    const [editScreenUrl, setEditScreenUrl] = useState('');
    const [updating, setUpdating] = useState(false);

    // For screen details view
    const [loadingDashboards, setLoadingDashboards] = useState(false);
    const [expandedPlaylists, setExpandedPlaylists] = useState({}); // מצב הרחבה של כל פלייליסט

    // For playlist dialogs
    const [editPlaylistDialogOpen, setEditPlaylistDialogOpen] = useState(false);
    const [editingPlaylist, setEditingPlaylist] = useState(null);
    const [editPlaylistName, setEditPlaylistName] = useState('');
    const [editPlaylistDescription, setEditPlaylistDescription] = useState('');
    const [updatingPlaylist, setUpdatingPlaylist] = useState(false);

    const [createPlaylistDialogOpen, setCreatePlaylistDialogOpen] = useState(false);
    const [newPlaylistName, setNewPlaylistName] = useState('');
    const [newPlaylistDescription, setNewPlaylistDescription] = useState('');
    const [creatingPlaylist, setCreatingPlaylist] = useState(false);

    // For template selector after creating playlist - reuse existing dialog
    const [isCreatingNewPlaylist, setIsCreatingNewPlaylist] = useState(false);

    // For delete operations
    const [deleting, setDeleting] = useState(false);

    // For delete dashboard dialog
    const [deleteDashboardDialogOpen, setDeleteDashboardDialogOpen] = useState(false);
    const [dashboardToDelete, setDashboardToDelete] = useState(null);
    const [playlistIdForDelete, setPlaylistIdForDelete] = useState(null);

    // For delete playlist dialog
    const [deletePlaylistDialogOpen, setDeletePlaylistDialogOpen] = useState(false);
    const [playlistToDelete, setPlaylistToDelete] = useState(null);

    // For delete screen dialog
    const [deleteScreenDialogOpen, setDeleteScreenDialogOpen] = useState(false);
    const [screenToDelete, setScreenToDelete] = useState(null);

    // For adding dashboard to playlist

    // For adding existing dashboard to playlist
    const [addExistingDashboardDialogOpen, setAddExistingDashboardDialogOpen] = useState(false);
    const [selectedPlaylistForExisting, setSelectedPlaylistForExisting] = useState(null);
    const [selectedExistingDashboardId, setSelectedExistingDashboardId] = useState(null);
    const [addingExistingDashboard, setAddingExistingDashboard] = useState(false);

    // For schedule calendar dialog
    const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);

    // For dashboard settings dialog
    const [dashboardSettingsDialogOpen, setDashboardSettingsDialogOpen] = useState(false);
    const [editingDashboard, setEditingDashboard] = useState(null);
    const [editingPlaylistId, setEditingPlaylistId] = useState(null);
    const [dashboardDuration, setDashboardDuration] = useState(30);
    const [dashboardTransition, setDashboardTransition] = useState('fade');
    const [updatingDashboardSettings, setUpdatingDashboardSettings] = useState(false);
    const [dashboardEnabled, setDashboardEnabled] = useState(true);

    // Toggle enabled state
    const [togglingDashboardId, setTogglingDashboardId] = useState(null);

    // Toast state for toggle feedback
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    // טעינת מסכים ודשבורדים
    useEffect(() => {
        if (organizationId) {
            dispatch(fetchScreens(organizationId));
            dispatch(fetchDashboards(organizationId)); // 🆕 טען כל הדשבורדים ברקע!
        }
    }, [organizationId, dispatch]);

    // טעינת פלייליסטים - תמיד טוען את כל הפלייליסטים של הארגון
    useEffect(() => {
        if (organizationId) {
            if (screenId) {
                // במצב detail view - טוען רק את הפלייליסטים של המסך הספציפי
                dispatch(fetchPlaylistsByScreen(screenId));
            } else {
                // במצב list view - טוען את כל הפלייליסטים של הארגון
                dispatch(fetchPlaylists(organizationId));
            }
        }
    }, [organizationId, screenId, dispatch]);

    // טעינת דשבורדים לכל פלייליסט (detail view)
    useEffect(() => {
        const loadDashboards = async () => {
            if (!currentScreen || !playlists.length || !screenId) return;

            // מניעת טעינה כפולה - בדיקה אם כבר טוענים
            if (loadingDashboards) {
                console.log('⏳ Already loading dashboards, skipping...');
                return;
            }

            console.log('🔄 useEffect - Loading dashboards...');
            console.log('📊 Current dashboards in Redux:', dashboards?.length || 0);
            console.log('📋 Current playlists:', playlists.length);

            setLoadingDashboards(true);
            try {
                // כל הפלייליסטים כבר מסוננים לפי screenId מה-API
                const relevantPlaylists = playlists;

                console.log('📋 Relevant playlists for this screen:', relevantPlaylists.length);

                // מיון הפלייליסטים לפי תאריך יצירה
                const sortedPlaylists = relevantPlaylists.sort((a, b) => {
                    const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
                    const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
                    return dateA - dateB;
                });

                // טעינת דשבורדים שחסרים בלבד
                const dashboardIdsToLoad = new Set();
                sortedPlaylists.forEach(playlist => {
                    console.log('📋 Checking playlist:', playlist.playlistId, 'items:', playlist.items);
                    (playlist.items || []).forEach(item => {
                        // בדיקה אם הדשבורד כבר קיים ב-Redux
                        const existsInRedux = dashboards?.some(d => d.dashboardId === item.dashboardId);
                        console.log(`🔍 Dashboard ${item.dashboardId} exists in Redux:`, existsInRedux);
                        if (!existsInRedux) {
                            dashboardIdsToLoad.add(item.dashboardId);
                        }
                    });
                });

                // טעינת דשבורדים חסרים בלבד - במקביל!
                if (dashboardIdsToLoad.size > 0) {
                    console.log('🔍 Loading missing dashboards in parallel:', Array.from(dashboardIdsToLoad));
                    const startTime = Date.now();

                    await Promise.all(
                        Array.from(dashboardIdsToLoad).map(dashboardId =>
                            dispatch(fetchDashboard(dashboardId)).catch(err => {
                                console.error(`Error loading dashboard ${dashboardId}:`, err);
                                return null;
                            })
                        )
                    );

                    const endTime = Date.now();
                    console.log(`✅ Loaded ${dashboardIdsToLoad.size} dashboards in ${endTime - startTime}ms`);
                } else {
                    console.log('✅ All dashboards already in Redux!');
                }

                // ⚠️ לא משתמשים ב-dashboards מה-closure!
                // במקום זה, נחכה ל-useEffect הבא שירוץ עם ה-dashboards המעודכנים
                console.log('✅ Dashboards loaded, waiting for Redux update...');
            } catch (error) {
                console.error('Error loading dashboards:', error);
            } finally {
                setLoadingDashboards(false);
            }
        };

        loadDashboards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentScreen?.screenId, playlists.length, screenId, dispatch]); // dependencies מצומצמות יותר

    // בניית המבנה של playlists עם dashboards באמצעות useMemo
    const playlistsWithDashboards = useMemo(() => {
        if (!currentScreen || !playlists.length || !screenId || !dashboards || loadingDashboards) {
            return [];
        }

        console.log('🏗️ Building playlists structure with dashboards...');
        console.log('📊 Available dashboards:', dashboards.length);

        // כל הפלייליסטים כבר מסוננים לפי screenId
        const relevantPlaylists = playlists;

        // מיון הפלייליסטים לפי תאריך יצירה (הראשון שנוצר = ראשון ברשימה)
        const sortedPlaylists = [...relevantPlaylists].sort((a, b) => {
            const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
            const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
            return dateA - dateB; // מהישן לחדש
        });

        // בניית המבנה מהדשבורדים שכבר ב-Redux
        const playlistsData = sortedPlaylists.map(playlist => {
            const playlistDashboards = (playlist.items || []).map((item, index) => {
                const dashboard = dashboards.find(d => d.dashboardId === item.dashboardId);
                console.log(`🔍 Looking for dashboard ${item.dashboardId}, found:`, !!dashboard);
                if (dashboard) {
                    console.log(`✅ Dashboard found:`, dashboard.name);
                }
                return dashboard ? {
                    ...dashboard,
                    order: index,
                    duration: item.duration || 30,
                    transition: item.transition || 'fade',
                    enabled: item.enabled ?? true
                } : null;
            }).filter(d => d !== null);

            console.log(`📋 Playlist ${playlist.name} has ${playlistDashboards.length} dashboards`);

            return {
                ...playlist,
                dashboards: playlistDashboards
            };
        });

        console.log('✅ All playlists with dashboards:', playlistsData);
        return playlistsData;
    }, [currentScreen, playlists, dashboards, screenId, loadingDashboards]);

    const handleScreenClick = (screenId) => {
        navigate(`/${organizationId}/screens/${screenId}`);
    };

    const handleAddScreenClick = () => {
        if (!isUnlimited(screensLimit) && screens.length >= screensLimit) {
            dispatch({ type: 'OPEN_UPGRADE_DRAWER', payload: { featureName: 'הוספת מסך', requiredPlan: 'pro' } });
            return;
        }
        setCreateDialogOpen(true);
    };

    const handleBack = () => {
        navigate(`/${organizationId}/screens`);
    };

    const handleDashboardClick = (dashboardId) => {
        // שמירת screenId ב-state כדי לאפשר חזרה
        navigate(`/${organizationId}/dashboard/${dashboardId}`, {
            state: { fromScreenId: screenId }
        });
    };

    const handlePreviewScreen = (screen, e) => {
        if (e) e.stopPropagation();
        const orgHash = organization?.orgHash || generateOrgHash(organizationId);
        const previewUrl = `${window.location.origin}/${orgHash}/${screen.url}`;
        window.open(previewUrl, '_blank');
    };

    // המרת עברית לאנגלית
    const hebrewToEnglish = (text) => {
        const map = {
            'א': 'a', 'ב': 'b', 'ג': 'g', 'ד': 'd', 'ה': 'h', 'ו': 'v', 'ז': 'z', 'ח': 'ch',
            'ט': 't', 'י': 'y', 'כ': 'k', 'ך': 'k', 'ל': 'l', 'מ': 'm', 'ם': 'm', 'נ': 'n',
            'ן': 'n', 'ס': 's', 'ע': '', 'פ': 'p', 'ף': 'f', 'צ': 'tz', 'ץ': 'tz', 'ק': 'k',
            'ר': 'r', 'ש': 'sh', 'ת': 't', ' ': '-', '_': '-'
        };

        let englishText = text.split('').map((char, index, arr) => {
            // לוגיקה חכמה לאות י'
            if (char === 'י') {
                // אם י' היא באמצע המילה, היא כנראה i (כמו ב-Gila)
                // אם היא בהתחלה, היא כנראה y (כמו ב-Yossi)
                // אם לפניה יש אות ניקוד (כמו א/ו), היא כנראה y (כמו ב-Mayim)
                const prevChar = index > 0 ? arr[index - 1] : '';
                if (index > 0 && !['א', 'ה', 'ו'].includes(prevChar)) {
                    return 'i';
                }
                return 'y';
            }

            // לוגיקה חכמה לאות ה'
            if (char === 'ה') {
                // ה' בסוף מילה היא בדרך כלל 'a' או 'ah' (כמו Gila / Sarah)
                if (index === arr.length - 1) {
                    return 'a';
                }
                return 'h';
            }

            // לוגיקה חכמה לאות ו'
            if (char === 'ו') {
                // ו' בתחילת מילה היא V (ורד), באמצע לרוב O או U. 
                // זה הימור, אבל O יותר נפוץ לשמות באמצע (Yossi, Moshe)
                if (index > 0) return 'o';
                return 'v';
            }

            return map[char] !== undefined ? map[char] : char;
        })
            .join('')
            .toLowerCase()
            .replace(/[^a-z0-9-]/g, '') // ניקוי תווים
            .replace(/-+/g, '-')        // מניעת כפילות מקפים
            .replace(/^-|-$/g, '');     // ניקוי מקפים בקצוות

        return englishText || `screen-${Math.random().toString(36).substr(2, 5)}`;
    };

    // יצירת hash קצר מ-organizationId
    const generateOrgHash = (orgId) => {
        let hash = 0;
        for (let i = 0; i < orgId.length; i++) {
            const char = orgId.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash;
        }
        return Math.abs(hash).toString(36).substring(0, 5);
    };

    // בדיקת ייחודיות URL
    const checkUrlUniqueness = (fullUrl, excludeScreenId = null) => {
        return !screens.some(screen => {
            const orgHash = organization?.orgHash || generateOrgHash(organizationId);
            const screenFullUrl = `${orgHash}/${screen.url}`;
            return screenFullUrl === fullUrl && screen.screenId !== excludeScreenId;
        });
    };

    // עדכון URL אוטומטי כשמשנים את שם המסך (יצירה)
    const handleNewScreenNameChange = (name) => {
        setNewScreenName(name);
        if (name.trim()) {
            const englishName = hebrewToEnglish(name.trim());
            setNewScreenUrl(englishName); // רק שם המסך, בלי hash
            setUrlError('');
        } else {
            setNewScreenUrl('');
            setUrlError('');
        }
    };

    // עדכון URL ידני (יצירה) - רק שם המסך
    const handleNewScreenUrlChange = (url) => {
        setNewScreenUrl(url);
        if (url.trim()) {
            const orgHash = organization?.orgHash || generateOrgHash(organizationId);
            const fullUrl = `${orgHash}/${url.trim()}`;
            const isUnique = checkUrlUniqueness(fullUrl);
            setUrlError(isUnique ? '' : 'הקישור כבר קיים במערכת');
        } else {
            setUrlError('');
        }
    };

    const handleNextCreateScreen = () => {
        if (!newScreenName.trim() || !newScreenUrl.trim()) return;

        // בניית URL מלא עם hash לבדיקת ייחודיות
        const orgHash = organization?.orgHash || generateOrgHash(organizationId);
        const fullUrl = `${orgHash}/${newScreenUrl.trim()}`;

        // בדיקת ייחודיות לפני שמירה
        if (!checkUrlUniqueness(fullUrl)) {
            setUrlError('הקישור כבר קיים במערכת');
            return;
        }

        // סגירת דיאלוג הפרטים ופתיחת בחירת תבנית
        setCreateDialogOpen(false);
        setNewScreenSelectedTemplateId(null);
        setNewScreenTemplateSelectorOpen(true);
    };

    const handleBackToScreenCreate = () => {
        setNewScreenTemplateSelectorOpen(false);
        setCreateDialogOpen(true);
    };

    const handleCreateScreenWithTemplate = async () => {
        if (!newScreenSelectedTemplateId) return;

        const isEmptyDashboard = newScreenSelectedTemplateId === 'empty';
        const isCopyFromExisting = newScreenSelectedTemplateId.startsWith('copy:');
        let templateWidgets = null;
        let templateSettings = null;

        if (isCopyFromExisting) {
            const sourceDashboardId = newScreenSelectedTemplateId.replace('copy:', '');
            const sourceDashboard = dashboards?.find(d => d.dashboardId === sourceDashboardId);
            if (sourceDashboard?.current) {
                templateWidgets = sourceDashboard.current.widgets || [];
                templateSettings = sourceDashboard.current.settings || null;
            }
        } else if (!isEmptyDashboard) {
            const template = predefinedTemplates.find(t => t.id === newScreenSelectedTemplateId);
            if (template) {
                templateWidgets = template.widgets || [];
                templateSettings = template.settings || null;
            }
        }

        try {
            setCreating(true);
            const newScreenData = {
                name: newScreenName.trim(),
                description: newScreenDescription.trim() || '',
                url: newScreenUrl.trim(),
                templateWidgets,
                templateSettings
            };

            const newScreen = await dispatch(createScreen(organizationId, newScreenData));

            setNewScreenTemplateSelectorOpen(false);
            setNewScreenName('');
            setNewScreenDescription('');
            setNewScreenUrl('');
            setUrlError('');
            setNewScreenSelectedTemplateId(null);

            // ניווט ישיר ללוח העריכה של הדשבורד החדש
            const createdDashboard = newScreen?.dashboard;
            if (createdDashboard?.dashboardId) {
                navigate(`/${organizationId}/dashboard/${createdDashboard.dashboardId}`, {
                    state: { fromScreenId: newScreen.screenId }
                });
            } else {
                navigate(`/${organizationId}/screens/${newScreen.screenId}`);
            }
        } catch (err) {
            console.error('Error creating screen:', err);
            alert('שגיאה ביצירת המסך');
        } finally {
            setCreating(false);
        }
    };

    const handleDeleteScreen = async (screenId, e) => {
        e.stopPropagation();

        // פתיחת דיאלוג אישור במקום window.confirm
        setScreenToDelete(screenId);
        setDeleteScreenDialogOpen(true);
    };

    const handleDeleteScreenConfirm = async () => {
        if (!screenToDelete) return;

        try {
            setDeleting(true);
            // מציאת המסך כדי לקבל את רשימת הפלייליסטים
            const screen = screens.find(s => s.screenId === screenToDelete);
            const playlistIds = [];

            if (screen?.schedule) {
                // תמיכה במבנה החדש (אובייקט) והישן (מערך)
                if (Array.isArray(screen.schedule)) {
                    // מבנה ישן: schedule הוא מערך
                    screen.schedule.forEach(item => {
                        if (item.playlistId && !playlistIds.includes(item.playlistId)) {
                            playlistIds.push(item.playlistId);
                        }
                    });
                } else {
                    // מבנה חדש: schedule הוא אובייקט עם מפתחות 0-6 (ימים)
                    for (let day = 0; day < 7; day++) {
                        const daySchedule = screen.schedule[day] || [];
                        daySchedule.forEach(item => {
                            if (item.playlistId && !playlistIds.includes(item.playlistId)) {
                                playlistIds.push(item.playlistId);
                            }
                        });
                    }
                }
            }

            await dispatch(deleteScreen(screenToDelete, playlistIds));
            console.log('✅ Screen deleted successfully');
        } catch (err) {
            console.error('Error deleting screen:', err);
            alert('שגיאה במחיקת המסך');
        } finally {
            setDeleting(false);
            setDeleteScreenDialogOpen(false);
            setScreenToDelete(null);
        }
    };

    const handleDeleteScreenCancel = () => {
        setDeleteScreenDialogOpen(false);
        setScreenToDelete(null);
    };

    const handleEditScreen = (screen, e) => {
        if (e) e.stopPropagation();
        setEditingScreen(screen);
        setEditScreenName(screen.name);
        setEditScreenDescription(screen.description || '');
        // המסך כבר מכיל רק את שם המסך (בלי hash)
        setEditScreenUrl(screen.url || '');
        setUrlError('');
        setEditDialogOpen(true);
    };

    const handleUpdateScreen = async () => {
        if (!editScreenName.trim()) {
            return;
        }

        try {
            setUpdating(true);
            const updates = {
                name: editScreenName.trim(),
                description: editScreenDescription.trim() || ''
            };

            // לא מעדכנים את ה-URL בעריכה - הוא נשאר כמו שהוא

            await dispatch(updateScreen(editingScreen.screenId, updates));

            setEditDialogOpen(false);
            setEditingScreen(null);
            setEditScreenName('');
            setEditScreenDescription('');
            setEditScreenUrl('');
            setUrlError('');
        } catch (err) {
            console.error('Error updating screen:', err);
            alert('שגיאה בעדכון המסך');
        } finally {
            setUpdating(false);
        }
    };

    const handleEditPlaylist = (playlist, e) => {
        if (e) e.stopPropagation();
        setEditingPlaylist(playlist);
        setEditPlaylistName(playlist.name);
        setEditPlaylistDescription(playlist.description || '');
        setEditPlaylistDialogOpen(true);
    };

    const handleUpdatePlaylist = async () => {
        if (!editPlaylistName.trim()) {
            return;
        }

        try {
            setUpdatingPlaylist(true);
            const updates = {
                name: editPlaylistName.trim(),
                description: editPlaylistDescription.trim() || ''
            };

            await dispatch(updatePlaylist(editingPlaylist.playlistId, updates));

            setEditPlaylistDialogOpen(false);
            setEditingPlaylist(null);
            setEditPlaylistName('');
            setEditPlaylistDescription('');
        } catch (err) {
            console.error('Error updating playlist:', err);
            alert('שגיאה בעדכון הפלייליסט');
        } finally {
            setUpdatingPlaylist(false);
        }
    };

    const handleCreatePlaylistClick = () => {
        if (!isUnlimited(playlistsPerScreenLimit) && playlists.length >= playlistsPerScreenLimit) {
            dispatch({ type: 'OPEN_UPGRADE_DRAWER', payload: { featureName: 'הוספת מערך תצוגה', requiredPlan: 'pro' } });
            return;
        }
        setCreatePlaylistDialogOpen(true);
    };

    const handleCreatePlaylist = async () => {
        if (!newPlaylistName.trim()) {
            return;
        }

        try {
            setCreatingPlaylist(true);

            // יצירת הפלייליסט ריק
            const playlistData = {
                name: newPlaylistName.trim(),
                description: newPlaylistDescription.trim() || '',
                items: [] // ריק בינתיים
            };

            // יצירת הפלייליסט והוספתו ל-schedule
            const newPlaylist = await dispatch(createPlaylist(organizationId, playlistData, currentScreen.screenId, currentScreen.schedule));

            // סגירת דיאלוג יצירת הפלייליסט
            setCreatePlaylistDialogOpen(false);
            setNewPlaylistName('');
            setNewPlaylistDescription('');

            // פתיחת בוחר התבניות עבור הפלייליסט החדש - reuse existing dialog
            setSelectedPlaylistForExisting(newPlaylist.playlistId);
            setSelectedExistingDashboardId(null);
            setIsCreatingNewPlaylist(true); // סימון שזה פלייליסט חדש
            setAddExistingDashboardDialogOpen(true);

        } catch (err) {
            console.error('Error creating playlist:', err);
            alert('שגיאה ביצירת הפלייליסט');
        } finally {
            setCreatingPlaylist(false);
        }
    };

    const handleDeletePlaylist = async (playlistId, e) => {
        if (e) e.stopPropagation();

        // פתיחת דיאלוג אישור במקום window.confirm
        setPlaylistToDelete(playlistId);
        setDeletePlaylistDialogOpen(true);
    };

    const handleDeletePlaylistConfirm = async () => {
        if (!playlistToDelete) return;

        try {
            setDeleting(true);
            // מחיקת הפלייליסט והסרתו מה-schedule (הכל באקשן אחד)
            await dispatch(deletePlaylist(playlistToDelete, currentScreen.screenId, currentScreen.schedule));
            console.log('✅ Playlist deleted successfully');
        } catch (err) {
            console.error('Error deleting playlist:', err);
            alert('שגיאה במחיקת הפלייליסט');
        } finally {
            setDeleting(false);
            setDeletePlaylistDialogOpen(false);
            setPlaylistToDelete(null);
        }
    };

    const handleDeletePlaylistCancel = () => {
        setDeletePlaylistDialogOpen(false);
        setPlaylistToDelete(null);
    };

    const handleAddExistingDashboard = async (playlistId, e) => {
        if (e) e.stopPropagation();
        const playlist = playlists.find(p => p.playlistId === playlistId);
        if (!isUnlimited(dashboardsPerPlaylistLimit) && (playlist?.items?.length ?? 0) >= dashboardsPerPlaylistLimit) {
            dispatch({ type: 'OPEN_UPGRADE_DRAWER', payload: { featureName: 'הוספת לוח', requiredPlan: 'pro' } });
            return;
        }
        setSelectedPlaylistForExisting(playlistId);
        setSelectedExistingDashboardId(null);
        setIsCreatingNewPlaylist(false); // זה לא פלייליסט חדש
        setAddExistingDashboardDialogOpen(true);
    };

    const handleSelectExistingDashboard = (dashboardId) => {
        setSelectedExistingDashboardId(dashboardId);
    };

    const handleConfirmAddExistingDashboard = async () => {
        if (!selectedExistingDashboardId || !selectedPlaylistForExisting) return;

        try {
            setAddingExistingDashboard(true);

            // מציאת הפלייליסט
            const playlist = playlists.find(p => p.playlistId === selectedPlaylistForExisting);
            if (!playlist) {
                throw new Error('Playlist not found');
            }

            // בדיקה איזה סוג נבחר - מסך ריק, תבנית, או העתקה מלוח קיים
            const isEmptyDashboard = selectedExistingDashboardId === 'empty';
            const isCopyFromExisting = selectedExistingDashboardId.startsWith('copy:');

            let dashboardName = 'לוח';
            let dashboardDescription = '';
            let templateWidgets = [];
            let templateSettings = { bgColor: '#fcfbfa', bgImage: '' };

            if (isEmptyDashboard) {
                dashboardName = 'לוח';
            } else if (isCopyFromExisting) {
                const sourceDashboardId = selectedExistingDashboardId.replace('copy:', '');
                const sourceDashboard = dashboards?.find(d => d.dashboardId === sourceDashboardId);
                if (sourceDashboard) {
                    dashboardName = `העתק - ${sourceDashboard.name || 'לוח'}`;
                    dashboardDescription = sourceDashboard.description || '';
                    templateWidgets = sourceDashboard.current?.widgets || [];
                    templateSettings = sourceDashboard.current?.settings || { bgColor: '#fcfbfa', bgImage: '' };
                }
            } else {
                const template = predefinedTemplates.find(t => t.id === selectedExistingDashboardId);
                if (template) {
                    dashboardName = template.name;
                    dashboardDescription = template.description || '';
                    templateWidgets = template.widgets || [];
                    templateSettings = template.settings || { bgColor: '#fcfbfa', bgImage: '' };
                }
            }

            // יצירת דשבורד חדש והוספתו לפלייליסט
            const newDashboard = await dispatch(createDashboardInPlaylist(
                organizationId,
                {
                    name: dashboardName,
                    description: dashboardDescription,
                    templateWidgets,
                    templateSettings
                },
                selectedPlaylistForExisting,
                playlist.items
            ));

            // סגירת הדיאלוג
            setAddExistingDashboardDialogOpen(false);
            setSelectedPlaylistForExisting(null);
            setSelectedExistingDashboardId(null);

            // מעבר ישיר לעריכת הדשבורד החדש
            navigate(`/${organizationId}/dashboard/${newDashboard.dashboardId}`, {
                state: { fromScreenId: screenId }
            });

        } catch (err) {
            console.error('❌ Error adding dashboard:', err);
            alert(`שגיאה בהוספת הלוח: ${err.message}`);
        } finally {
            setAddingExistingDashboard(false);
        }
    };

    // Handler לעדכון הגדרות דשבורד בפלייליסט
    const handleOpenDashboardSettings = (playlistId, dashboard, e) => {
        if (e) e.stopPropagation();
        setEditingPlaylistId(playlistId);
        setEditingDashboard(dashboard);
        setDashboardDuration(dashboard.duration || 30);
        setDashboardTransition(dashboard.transition || 'fade');
        setDashboardEnabled(dashboard.enabled ?? true);
        setDashboardSettingsDialogOpen(true);
    };

    const handleUpdateDashboardSettings = async () => {
        if (!editingPlaylistId || !editingDashboard) return;

        try {
            setUpdatingDashboardSettings(true);
            console.log('🔧 Updating dashboard settings:', {
                playlistId: editingPlaylistId,
                dashboardId: editingDashboard.dashboardId,
                duration: dashboardDuration,
                transition: dashboardTransition
            });

            // מציאת הפלייליסט
            const playlist = playlists.find(p => p.playlistId === editingPlaylistId);
            if (!playlist) {
                throw new Error('Playlist not found');
            }

            // עדכון ה-items עם ההגדרות החדשות
            const updatedItems = playlist.items.map(item => {
                if (item.dashboardId === editingDashboard.dashboardId) {
                    return {
                        ...item,
                        duration: dashboardDuration,
                        transition: dashboardTransition,
                        enabled: dashboardEnabled
                    };
                }
                return item;
            });

            // עדכון הפלייליסט ב-Firestore
            await dispatch(updatePlaylist(editingPlaylistId, { items: updatedItems }));

            console.log('✅ Dashboard settings updated successfully');

            // סגירת הדיאלוג
            setDashboardSettingsDialogOpen(false);
            setEditingDashboard(null);
            setEditingPlaylistId(null);

        } catch (err) {
            console.error('❌ Error updating dashboard settings:', err);
            alert(`שגיאה בעדכון הגדרות הלוח: ${err.message}`);
        } finally {
            setUpdatingDashboardSettings(false);
        }
    };

    const handleToggleDashboardEnabled = async (playlistId, dashboard, e) => {
        if (e) e.stopPropagation();
        const playlist = playlists.find(p => p.playlistId === playlistId);
        if (!playlist) return;

        setTogglingDashboardId(dashboard.dashboardId);

        const newEnabled = !(dashboard.enabled ?? true);
        const updatedItems = playlist.items.map(item => {
            if (item.dashboardId === dashboard.dashboardId) {
                return { ...item, enabled: newEnabled };
            }
            return item;
        });

        try {
            await dispatch(updatePlaylist(playlistId, { items: updatedItems }));
            setToastMessage(newEnabled ? 'הלוח הופעל' : 'הלוח הושבת — לא יוצג בתצוגה');
            setToastOpen(true);
        } catch (err) {
            console.error('❌ Error toggling dashboard enabled:', err);
            alert(`שגיאה בעדכון סטטוס הלוח: ${err.message}`);
        } finally {
            setTogglingDashboardId(null);
        }
    };

    const handleDeleteDashboard = async (playlistId, dashboardId, e) => {
        if (e) e.stopPropagation();

        console.log('🗑️ handleDeleteDashboard called:', { playlistId, dashboardId });

        // פתיחת דיאלוג אישור במקום window.confirm
        setDashboardToDelete(dashboardId);
        setPlaylistIdForDelete(playlistId);
        setDeleteDashboardDialogOpen(true);

        console.log('✅ Dialog should be open now');
    };

    const handleDeleteDashboardConfirm = async () => {
        if (!dashboardToDelete || !playlistIdForDelete) return;

        try {
            setDeleting(true);

            // מציאת הפלייליסט
            const playlist = playlists.find(p => p.playlistId === playlistIdForDelete);
            if (!playlist) {
                throw new Error('Playlist not found');
            }

            // מחיקת הדשבורד מהפלייליסט
            await dispatch(deleteDashboardFromPlaylist(dashboardToDelete, playlistIdForDelete, playlist.items));

            console.log('✅ Dashboard deleted successfully');
        } catch (err) {
            console.error('❌ Error deleting dashboard:', err);
            alert(`שגיאה במחיקת הלוח: ${err.message}`);
        } finally {
            setDeleting(false);
            setDeleteDashboardDialogOpen(false);
            setDashboardToDelete(null);
            setPlaylistIdForDelete(null);
        }
    };

    const handleDeleteDashboardCancel = () => {
        setDeleteDashboardDialogOpen(false);
        setDashboardToDelete(null);
        setPlaylistIdForDelete(null);
    };

    /**
     * שמירת לוח זמנים מעודכן
     */
    const handleSaveSchedule = async (newSchedule) => {
        try {
            console.log('🟢 Screen: handleSaveSchedule called');
            console.log('💾 Saving schedule:', newSchedule);
            const startTime = Date.now();

            // קריאה ל-action שמעדכן Redux אופטימיסטית ושומר ב-Firestore
            console.log('🟢 Screen: Calling dispatch(updateScreen)...');
            const result = await dispatch(updateScreen(currentScreen.screenId, { schedule: newSchedule }));
            console.log('🟢 Screen: dispatch completed!', result);

            console.log(`✅ Schedule saved in ${Date.now() - startTime}ms`);

            // חזרה מפורשת של success - כדי שה-ScheduleEditor ידע שהשמירה הצליחה
            return true;
        } catch (error) {
            console.error('❌ Error saving schedule:', error);
            throw error;
        }
    };

    // Loading state
    if (screensLoading) {
        return <LoadingOverlay open={true} />;
    }

    // Error state
    if (screensError) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="error">{screensError}</Alert>
                {isDetailView && (
                    <Button onClick={handleBack} sx={{ mt: 2 }}>
                        חזור למסכים
                    </Button>
                )}
            </Box>
        );
    }

    // Detail View - Screen Details with Playlists
    if (isDetailView) {
        if (!currentScreen) {
            return (
                <Box sx={{ p: 4 }}>
                    <Alert severity="warning">המסך לא נמצא</Alert>
                    <Button onClick={handleBack} sx={{ mt: 2 }}>
                        חזור למסכים
                    </Button>
                </Box>
            );
        }

        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#fcfbfa' }}>
                {/* Header */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        background: 'linear-gradient(135deg, rgba(30,58,95,0.04) 0%, rgba(255,255,255,0) 70%)',
                        bgcolor: 'white',
                        borderBottom: '1px solid rgba(30,58,95,0.09)',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <IconButton onClick={handleBack} sx={{ color: '#1e3a5f' }}>
                            <ArrowForwardIcon />
                        </IconButton>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1e3a5f' }}>
                                {currentScreen.name}
                            </Typography>
                            {currentScreen.description && (
                                <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                                    {currentScreen.description}
                                </Typography>
                            )}
                        </Box>
                        <Button
                            variant="outlined"
                            onClick={(e) => handlePreviewScreen(currentScreen, e)}
                            sx={{
                                borderColor: 'rgba(30,58,95,0.4)',
                                color: '#1e3a5f',
                                display: 'flex',
                                flexDirection: 'row',
                                gap: 1.5,
                                borderRadius: '8px',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    borderColor: '#1e3a5f',
                                    bgcolor: 'rgba(30,58,95,0.05)',
                                    boxShadow: '0 2px 10px rgba(30,58,95,0.12)',
                                    transform: 'translateY(-1px)'
                                }
                            }}
                        >
                            <VisibilityIcon fontSize="small" />
                            <Box component="span">תצוגה מקדימה</Box>
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => handleEditScreen(currentScreen)}
                            sx={{
                                borderColor: 'rgba(30,58,95,0.4)',
                                color: '#1e3a5f',
                                display: 'flex',
                                flexDirection: 'row',
                                gap: 1.5,
                                borderRadius: '8px',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    borderColor: '#1e3a5f',
                                    bgcolor: 'rgba(30,58,95,0.05)',
                                    boxShadow: '0 2px 10px rgba(30,58,95,0.12)',
                                    transform: 'translateY(-1px)'
                                }
                            }}
                        >
                            <EditIcon fontSize="small" />
                            <Box component="span">ערוך מסך</Box>
                        </Button>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexDirection: 'row-reverse', justifyContent: 'flex-end' }}>
                        <Chip
                            label={`${playlistsWithDashboards.reduce((sum, p) => sum + (p.dashboards?.length || 0), 0)} לוחות`}
                            icon={<DashboardIcon />}
                            sx={{
                                bgcolor: 'rgba(30,58,95,0.07)',
                                color: '#1e3a5f',
                                border: '1px solid rgba(30,58,95,0.12)',
                                fontWeight: 500,
                                display: 'flex',
                                flexDirection: 'row',
                                '& .MuiChip-icon': {
                                    color: '#1e3a5f',
                                    opacity: 0.7,
                                    marginRight: '12px',
                                    marginLeft: 0,
                                    order: 1
                                },
                                '& .MuiChip-label': {
                                    order: 2,
                                    paddingRight: 0
                                }
                            }}
                        />
                        <Chip
                            label={`${playlistsWithDashboards.length} מערכי תצוגה`}
                            icon={<PlaylistPlayIcon />}
                            sx={{
                                bgcolor: 'rgba(154,114,9,0.08)',
                                color: '#7a5a07',
                                border: '1px solid rgba(154,114,9,0.18)',
                                fontWeight: 500,
                                display: 'flex',
                                flexDirection: 'row',
                                '& .MuiChip-icon': {
                                    color: '#9a7209',
                                    opacity: 0.8,
                                    marginRight: '12px',
                                    marginLeft: 0,
                                    order: 1
                                },
                                '& .MuiChip-label': {
                                    order: 2,
                                    paddingRight: 0
                                }
                            }}
                        />
                    </Box>
                </Paper>

                {/* Playlists */}
                <Box sx={{ p: 2, overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
                    {!loadingDashboards && playlistsWithDashboards.length === 0 ? (
                        <Paper
                            sx={{
                                p: 6,
                                textAlign: 'center',
                                bgcolor: 'white',
                                borderRadius: '12px',
                                border: '2px dashed rgba(30,58,95,0.18)'
                            }}
                        >
                            <PlaylistPlayIcon sx={{ fontSize: 80, color: '#ccc', mb: 2 }} />
                            <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
                                אין מערכי תצוגה במסך זה
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#999', mb: 3 }}>
                                הוסף מערך תצוגה כדי להתחיל להציג תוכן
                            </Typography>
                        </Paper>
                    ) : (
                        <Stack spacing={1.5}>
                            {/* הסבר על מערכי תצוגה */}
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    bgcolor: '#f8fafc',
                                    borderRadius: '12px',
                                    border: '1px solid #e2e8f0'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e3a5f', mb: 0.5, fontSize: '1.15rem' }}>
                                            מערכי תצוגה
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.8, fontSize: '0.875rem' }}>
                                            כל מערך תצוגה מכיל בדרך כלל <strong>לוח אחד</strong> שמוצג על המסך.
                                            <br />
                                            רוצים שהמסך יציג כמה לוחות שמתחלפים ביניהם? אפשר <strong>להוסיף עוד לוחות לאותו המערך</strong> — כל לוח יוצג למשך הזמן שקבעתם בהגדרותיו, ואז יעבור אוטומטית לבא.
                                            <br />
                                            רוצים <strong>תצוגה שונה לגמרי</strong> בזמנים מסוימים — לשבת, לחג, לקמפיין? <strong>צרו מערך נוסף.</strong> כל מערך הוא עולם בפני עצמו.
                                            <br />
                                            לחצו על <strong>"ניהול זמנים"</strong> כדי לקבוע מתי יופעל כל מערך — מכאן המסך מתחלף לבד.
                                        </Typography>
                                    </Box>
                                    {/* כפתורים */}
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        {/* כפתור צור מערך תצוגה */}
                                        {(() => {
                                            const playlistLimitReached = !isUnlimited(playlistsPerScreenLimit) && playlists.length >= playlistsPerScreenLimit;
                                            return (
                                                <Tooltip title={playlistLimitReached ? 'הגעת למגבלת התוכנית — לחץ לשדרוג' : ''} arrow>
                                                    <span>
                                                        <Button
                                                            variant="outlined"
                                                            onClick={handleCreatePlaylistClick}
                                                            disabled={playlistLimitReached}
                                                            sx={{
                                                                borderColor: 'rgba(30,58,95,0.4)',
                                                                color: '#1e3a5f',
                                                                display: 'flex',
                                                                flexDirection: 'row',
                                                                gap: 1.5,
                                                                borderRadius: '8px',
                                                                whiteSpace: 'nowrap',
                                                                transition: 'all 0.2s',
                                                                '&:hover': {
                                                                    borderColor: '#1e3a5f',
                                                                    bgcolor: 'rgba(30,58,95,0.05)',
                                                                    boxShadow: '0 2px 10px rgba(30,58,95,0.12)',
                                                                    transform: 'translateY(-1px)'
                                                                },
                                                                '&.Mui-disabled': {
                                                                    borderColor: '#cbd5e1',
                                                                    color: '#94a3b8'
                                                                }
                                                            }}
                                                        >
                                                            <AddIcon fontSize="small" />
                                                            <Box component="span">צור מערך תצוגה</Box>
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                            );
                                        })()}

                                        {/* כפתור ערוך לוח זמנים - תמיד מוצג, disabled כשיש רק מערך תצוגה אחד */}
                                        <Tooltip
                                            title={playlistsWithDashboards.length === 1 ? "צרו לפחות שני מערכי תצוגה כדי לנהל לוח זמנים" : ""}
                                            arrow
                                        >
                                            <span>
                                                <Button
                                                    variant="outlined"
                                                    onClick={() => setScheduleDialogOpen(true)}
                                                    disabled={playlistsWithDashboards.length === 1}
                                                    sx={{
                                                        borderColor: 'rgba(30,58,95,0.4)',
                                                        color: '#1e3a5f',
                                                        display: 'flex',
                                                        flexDirection: 'row',
                                                        gap: 1.5,
                                                        borderRadius: '8px',
                                                        whiteSpace: 'nowrap',
                                                        transition: 'all 0.2s',
                                                        '&:hover': {
                                                            borderColor: '#1e3a5f',
                                                            bgcolor: 'rgba(30,58,95,0.05)',
                                                            boxShadow: '0 2px 10px rgba(30,58,95,0.12)',
                                                            transform: 'translateY(-1px)'
                                                        },
                                                        '&.Mui-disabled': {
                                                            borderColor: '#cbd5e1',
                                                            color: '#94a3b8'
                                                        }
                                                    }}
                                                >
                                                    <CalendarTodayIcon fontSize="small" />
                                                    <Box component="span">ערוך לוח זמנים</Box>
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Paper>

                            {playlistsWithDashboards.map((playlist) => {
                                // אם יש רק מערך תצוגה אחד - תמיד פתוח
                                // אם יש יותר - לפי מצב ה-state
                                const isExpanded = playlistsWithDashboards.length === 1
                                    ? true
                                    : (expandedPlaylists[playlist.playlistId] || false);

                                const hasMultiplePlaylists = playlistsWithDashboards.length > 1;

                                return (
                                    <Paper
                                        key={playlist.playlistId}
                                        elevation={2}
                                        sx={{
                                            borderRadius: '12px',
                                            bgcolor: 'white',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        {/* Playlist Header - Always Visible */}
                                        <Box
                                            sx={{
                                                p: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                // רק אם יש יותר מפלייליסט אחד - אפשר ללחוץ
                                                cursor: hasMultiplePlaylists ? 'pointer' : 'default',
                                                '&:hover': hasMultiplePlaylists ? { bgcolor: '#f5f5f5' } : {}
                                            }}
                                            onClick={() => {
                                                // רק אם יש יותר מפלייליסט אחד - מאפשר לחיצה
                                                if (hasMultiplePlaylists) {
                                                    setExpandedPlaylists(prev => ({
                                                        ...prev,
                                                        [playlist.playlistId]: !prev[playlist.playlistId]
                                                    }));
                                                }
                                            }}
                                        >
                                            {/* מציג חץ רק אם יש יותר מפלייליסט אחד */}
                                            {hasMultiplePlaylists && (
                                                <IconButton size="small" sx={{ color: '#1e3a5f' }}>
                                                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                </IconButton>
                                            )}
                                            <Avatar sx={{ background: 'linear-gradient(135deg, #1a3352 0%, #1e3a5f 100%)', boxShadow: '0 2px 8px rgba(30,58,95,0.25)', width: 40, height: 40 }}>
                                                <PlaylistPlayIcon />
                                            </Avatar>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e3a5f', fontSize: '1rem', mb: 0.5 }}>
                                                    {playlist.name}
                                                </Typography>
                                                {/* אינדיקטור לוח זמנים */}
                                                <PlaylistScheduleIndicator
                                                    schedule={currentScreen.schedule}
                                                    playlistId={playlist.playlistId}
                                                />
                                                {playlist.description && (
                                                    <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem', mt: 0.5 }}>
                                                        {playlist.description}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Chip
                                                label={`${playlist.dashboards?.length || 0} לוחות`}
                                                icon={<DashboardIcon />}
                                                size="small"
                                                sx={{
                                                    bgcolor: 'rgba(30,58,95,0.07)',
                                                    color: '#1e3a5f',
                                                    border: '1px solid rgba(30,58,95,0.12)',
                                                    fontWeight: 500,
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    '& .MuiChip-icon': {
                                                        color: '#1e3a5f',
                                                        opacity: 0.65,
                                                        marginRight: '8px',
                                                        marginLeft: 0,
                                                        order: 1
                                                    },
                                                    '& .MuiChip-label': {
                                                        order: 2,
                                                        paddingRight: 0
                                                    }
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                            <IconButton
                                                size="small"
                                                sx={{ color: '#1e3a5f' }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEditPlaylist(playlist, e);
                                                }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                sx={{ color: '#d32f2f' }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeletePlaylist(playlist.playlistId, e);
                                                }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>

                                        {/* Dashboards Grid - Collapsible */}
                                        <Collapse in={isExpanded}>
                                            <Box sx={{ p: 2, pt: 0 }}>
                                                <Divider sx={{ mb: 2 }} />

                                                {/* Dashboards Grid */}
                                                {playlist.dashboards && playlist.dashboards.length > 0 ? (
                                                    <Box
                                                        sx={{
                                                            display: 'grid',
                                                            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                                                            gap: 1.5,
                                                            maxWidth: '100%'
                                                        }}
                                                    >
                                                        {playlist.dashboards.map((dashboard) => {
                                                            const hasMultipleDashboards = playlist.dashboards.length > 1;
                                                            const isDashboardEnabled = dashboard.enabled ?? true;

                                                            return (
                                                                <Card
                                                                    key={dashboard.dashboardId}
                                                                    sx={{
                                                                        borderRadius: '10px',
                                                                        transition: 'all 0.25s',
                                                                        opacity: isDashboardEnabled ? 1 : 0.5,
                                                                        '&:hover': {
                                                                            transform: 'translateY(-3px)',
                                                                            boxShadow: '0 8px 24px rgba(30,58,95,0.14)'
                                                                        }
                                                                    }}
                                                                >
                                                                    <CardActionArea onClick={() => handleDashboardClick(dashboard.dashboardId)}>
                                                                        <Box
                                                                            sx={{
                                                                                height: 120,
                                                                                position: 'relative',
                                                                                overflow: 'hidden',
                                                                                border: '1px solid #eee',
                                                                                bgcolor: '#f5f5f5'
                                                                            }}
                                                                        >
                                                                            {/* iframe תצוגה מקדימה */}
                                                                            <iframe
                                                                                src={`/${organizationId}/view/${dashboard.dashboardId}`}
                                                                                style={{
                                                                                    width: '100%',
                                                                                    height: '100%',
                                                                                    border: 'none',
                                                                                    position: 'absolute',
                                                                                    top: 0,
                                                                                    left: 0,
                                                                                    pointerEvents: 'none'
                                                                                }}
                                                                                title={`Preview ${dashboard.name}`}
                                                                            />

                                                                            {/* Overlay שקוף לחסימת אינטראקציה */}
                                                                            <Box
                                                                                sx={{
                                                                                    position: 'absolute',
                                                                                    top: 0,
                                                                                    left: 0,
                                                                                    right: 0,
                                                                                    bottom: 0,
                                                                                    bgcolor: 'transparent',
                                                                                    pointerEvents: 'auto',
                                                                                    cursor: 'pointer'
                                                                                }}
                                                                            />
                                                                        </Box>

                                                                        <CardContent sx={{ py: 1.5, px: 2 }}>
                                                                            {/* שורה עליונה: שם ומשך */}
                                                                            <Box sx={{ mb: 1 }}>
                                                                                <Typography
                                                                                    variant="subtitle1"
                                                                                    sx={{
                                                                                        fontWeight: 'bold',
                                                                                        mb: hasMultipleDashboards ? 0.5 : 0,
                                                                                        overflow: 'hidden',
                                                                                        textOverflow: 'ellipsis',
                                                                                        whiteSpace: 'nowrap'
                                                                                    }}
                                                                                >
                                                                                    {dashboard.name || `Dashboard ${dashboard.order + 1}`}
                                                                                </Typography>
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                    {hasMultipleDashboards && (
                                                                                        <Typography variant="caption" sx={{ color: '#999' }}>
                                                                                            משך: {dashboard.duration || 30} שניות
                                                                                        </Typography>
                                                                                    )}
                                                                                    {!isDashboardEnabled && (
                                                                                        <Chip
                                                                                            label="מושבת"
                                                                                            size="small"
                                                                                            sx={{
                                                                                                bgcolor: '#fff3e0',
                                                                                                color: '#e65100',
                                                                                                fontSize: '0.7rem',
                                                                                                height: '20px',
                                                                                                fontWeight: 600
                                                                                            }}
                                                                                        />
                                                                                    )}
                                                                                </Box>
                                                                            </Box>

                                                                            {/* שורה תחתונה: ווידג'טים משמאל, כפתורים מימין */}
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                                                {/* מספר ווידג'טים */}
                                                                                <Chip
                                                                                    label={`${dashboard.current?.widgets?.length || dashboard.widgets?.length || 0} רכיבים`}
                                                                                    size="small"
                                                                                    icon={<DashboardIcon sx={{ fontSize: '0.9rem !important' }} />}
                                                                                    sx={{
                                                                                        bgcolor: '#e3f2fd',
                                                                                        fontSize: '0.75rem',
                                                                                        height: '24px',
                                                                                        '& .MuiChip-icon': {
                                                                                            marginRight: '4px',
                                                                                            marginLeft: 0
                                                                                        }
                                                                                    }}
                                                                                />

                                                                                {/* כפתורים */}
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                    {/* Toggle פעיל/מושבת */}
                                                                                    {togglingDashboardId === dashboard.dashboardId ? (
                                                                                        <CircularProgress size={20} sx={{ color: '#1e3a5f', mx: 1 }} />
                                                                                    ) : (
                                                                                        <Tooltip title={isDashboardEnabled ? 'לוח פעיל' : 'לוח מושבת'} placement="top">
                                                                                            <Switch
                                                                                                size="small"
                                                                                                checked={isDashboardEnabled}
                                                                                                onClick={(e) => handleToggleDashboardEnabled(playlist.playlistId, dashboard, e)}
                                                                                                sx={{
                                                                                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                                                                                        color: '#1e3a5f'
                                                                                                    },
                                                                                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                                                        backgroundColor: '#1e3a5f'
                                                                                                    }
                                                                                                }}
                                                                                            />
                                                                                        </Tooltip>
                                                                                    )}
                                                                                    {/* כפתור הגדרות - רק אם יש יותר מדשבורד אחד */}
                                                                                    {hasMultipleDashboards && (
                                                                                        <Tooltip title="הגדרות תצוגה" placement="top">
                                                                                            <IconButton
                                                                                                size="small"
                                                                                                onClick={(e) => handleOpenDashboardSettings(playlist.playlistId, dashboard, e)}
                                                                                                sx={{
                                                                                                    color: '#1e3a5f',
                                                                                                    padding: '4px',
                                                                                                    '&:hover': {
                                                                                                        bgcolor: 'rgba(30,58,95,0.08)'
                                                                                                    }
                                                                                                }}
                                                                                            >
                                                                                                <SettingsIcon sx={{ fontSize: '1.1rem' }} />
                                                                                            </IconButton>
                                                                                        </Tooltip>
                                                                                    )}

                                                                                    {/* כפתור מחיקה */}
                                                                                    <Tooltip title="מחק לוח" placement="top">
                                                                                        <IconButton
                                                                                            size="small"
                                                                                            onClick={(e) => handleDeleteDashboard(playlist.playlistId, dashboard.dashboardId, e)}
                                                                                            sx={{
                                                                                                color: '#d32f2f',
                                                                                                padding: '4px',
                                                                                                '&:hover': {
                                                                                                    bgcolor: 'rgba(211,47,47,0.08)'
                                                                                                }
                                                                                            }}
                                                                                        >
                                                                                            <DeleteIcon sx={{ fontSize: '1.1rem' }} />
                                                                                        </IconButton>
                                                                                    </Tooltip>
                                                                                </Box>
                                                                            </Box>
                                                                        </CardContent>
                                                                    </CardActionArea>
                                                                </Card>
                                                            )
                                                        })}

                                                        {/* Add Dashboard Card - Single Button */}
                                                        {(() => {
                                                            const dashLimitReached = !isUnlimited(dashboardsPerPlaylistLimit) && (playlist.items?.length ?? 0) >= dashboardsPerPlaylistLimit;
                                                            return (
                                                                <Tooltip title={dashLimitReached ? 'הגעת למגבלת התוכנית — לחץ לשדרוג' : ''} arrow>
                                                                    <Card
                                                                        sx={{
                                                                            borderRadius: '8px',
                                                                            border: '2px dashed rgba(30,58,95,0.3)',
                                                                            bgcolor: 'rgba(30,58,95,0.03)',
                                                                            opacity: dashLimitReached ? 0.5 : 1,
                                                                            cursor: dashLimitReached ? 'not-allowed' : 'pointer'
                                                                        }}
                                                                    >
                                                                        <CardActionArea
                                                                            onClick={(e) => handleAddExistingDashboard(playlist.playlistId, e)}
                                                                            sx={{
                                                                                height: '100%',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                minHeight: 180
                                                                            }}
                                                                        >
                                                                            <Box sx={{ textAlign: 'center', p: 2 }}>
                                                                                <AddIcon sx={{ fontSize: 36, color: '#1e3a5f', opacity: 0.5, mb: 1 }} />
                                                                                <Typography variant="body2" sx={{ color: '#1e3a5f', fontSize: '0.875rem', fontWeight: 600 }}>
                                                                                    הוסף לוח
                                                                                </Typography>
                                                                            </Box>
                                                                        </CardActionArea>
                                                                    </Card>
                                                                </Tooltip>
                                                            );
                                                        })()}
                                                    </Box>
                                                ) : (
                                                    <Box
                                                        sx={{
                                                            textAlign: 'center',
                                                            py: 4,
                                                            bgcolor: '#fafafa',
                                                            borderRadius: '8px',
                                                            border: '1px dashed #ddd'
                                                        }}
                                                    >
                                                        <Typography variant="body2" sx={{ color: '#999' }}>
                                                            אין לוחות במערך תצוגה זה
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Collapse>
                                    </Paper>
                                );
                            })}
                        </Stack>
                    )}
                </Box>

                {/* Edit Screen Dialog */}
                <ConfigDialog
                    open={editDialogOpen}
                    onClose={() => !updating && setEditDialogOpen(false)}
                    onConfirm={handleUpdateScreen}
                    title="ערוך מסך"
                    confirmText={updating ? 'מעדכן...' : 'עדכן'}
                    cancelText="ביטול"
                    isLoading={updating}
                    confirmDisabled={!editScreenName.trim()}
                    width={500}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            autoFocus
                            label="שם המסך"
                            fullWidth
                            value={editScreenName}
                            onChange={(e) => setEditScreenName(e.target.value)}
                            disabled={updating}
                            required
                        />
                        <TextField
                            label="תיאור (אופציונלי)"
                            fullWidth
                            multiline
                            rows={2}
                            value={editScreenDescription}
                            onChange={(e) => setEditScreenDescription(e.target.value)}
                            disabled={updating}
                        />

                        {/* URL Section - Read Only */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                bgcolor: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: 1
                            }}
                        >
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 1 }}>
                                קישור המסך
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: 'white', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: '#1e293b',
                                        fontSize: '0.875rem',
                                        flex: 1,
                                        direction: 'ltr',
                                        textAlign: 'left',
                                        wordBreak: 'break-all'
                                    }}
                                >
                                    {window.location.origin}/{organization?.orgHash || generateOrgHash(organizationId)}/{editScreenUrl}
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        const orgHash = organization?.orgHash || generateOrgHash(organizationId);
                                        navigator.clipboard.writeText(`${window.location.origin}/${orgHash}/${editScreenUrl}`);
                                        setCopySuccess(true);
                                        setTimeout(() => setCopySuccess(false), 2000);
                                    }}
                                    sx={{ color: '#1e3a5f' }}
                                >
                                    {copySuccess ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                </IconButton>
                            </Box>
                            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5 }}>
                                הקישור נוצר אוטומטית ולא ניתן לעריכה
                            </Typography>
                        </Paper>
                    </Box>
                </ConfigDialog>

                {/* Edit Playlist Dialog */}
                <ConfigDialog
                    open={editPlaylistDialogOpen}
                    onClose={() => !updatingPlaylist && setEditPlaylistDialogOpen(false)}
                    onConfirm={handleUpdatePlaylist}
                    title="ערוך מערך תצוגה"
                    confirmText={updatingPlaylist ? 'מעדכן...' : 'עדכן'}
                    cancelText="ביטול"
                    isLoading={updatingPlaylist}
                    confirmDisabled={!editPlaylistName.trim()}
                    width={500}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            autoFocus
                            label="שם מערך התצוגה"
                            fullWidth
                            value={editPlaylistName}
                            onChange={(e) => setEditPlaylistName(e.target.value)}
                            disabled={updatingPlaylist}
                            required
                        />
                        <TextField
                            label="תיאור (אופציונלי)"
                            fullWidth
                            multiline
                            rows={3}
                            value={editPlaylistDescription}
                            onChange={(e) => setEditPlaylistDescription(e.target.value)}
                            disabled={updatingPlaylist}
                        />
                    </Box>
                </ConfigDialog>

                {/* Create Playlist Dialog */}
                <ConfigDialog
                    open={createPlaylistDialogOpen}
                    onClose={() => !creatingPlaylist && setCreatePlaylistDialogOpen(false)}
                    onConfirm={handleCreatePlaylist}
                    title="צור מערך תצוגה חדש"
                    confirmText={creatingPlaylist ? 'יוצר...' : 'צור'}
                    cancelText="ביטול"
                    isLoading={creatingPlaylist}
                    confirmDisabled={!newPlaylistName.trim()}
                    width={500}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            autoFocus
                            label="שם מערך התצוגה"
                            fullWidth
                            value={newPlaylistName}
                            onChange={(e) => setNewPlaylistName(e.target.value)}
                            disabled={creatingPlaylist}
                            required
                        />
                        <TextField
                            label="תיאור (אופציונלי)"
                            fullWidth
                            multiline
                            rows={3}
                            value={newPlaylistDescription}
                            onChange={(e) => setNewPlaylistDescription(e.target.value)}
                            disabled={creatingPlaylist}
                        />
                    </Box>
                </ConfigDialog>

                {/* Dashboard Settings Dialog */}
                <ConfigDialog
                    open={dashboardSettingsDialogOpen}
                    onClose={() => !updatingDashboardSettings && setDashboardSettingsDialogOpen(false)}
                    onConfirm={handleUpdateDashboardSettings}
                    title={`הגדרות: ${editingDashboard?.name || 'לוח'}`}
                    confirmText={updatingDashboardSettings ? 'שומר...' : 'שמור'}
                    cancelText="ביטול"
                    isLoading={updatingDashboardSettings}
                    width={500}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* Enabled Toggle */}
                        <Box>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={dashboardEnabled}
                                        onChange={(e) => setDashboardEnabled(e.target.checked)}
                                        disabled={updatingDashboardSettings}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#1e3a5f' },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#1e3a5f' }
                                        }}
                                    />
                                }
                                label="לוח פעיל"
                                sx={{ '& .MuiFormControlLabel-label': { fontWeight: 'bold', color: '#1e3a5f' } }}
                            />
                            <Typography variant="caption" sx={{ color: '#999', display: 'block', mr: 6 }}>
                                השינוי יישמר לאחר לחיצה על שמור
                            </Typography>
                        </Box>

                        {/* Duration Slider */}
                        <Box>
                            <Typography gutterBottom sx={{ fontWeight: 'bold', mb: 2, color: '#1e3a5f' }}>
                                משך תצוגה: {dashboardDuration} שניות
                            </Typography>
                            <Slider
                                value={dashboardDuration}
                                onChange={(e, newValue) => setDashboardDuration(newValue)}
                                min={5}
                                max={300}
                                step={5}
                                marks={[
                                    { value: 5, label: '5s' },
                                    { value: 30, label: '30s' },
                                    { value: 60, label: '1m' },
                                    { value: 120, label: '2m' },
                                    { value: 300, label: '5m' }
                                ]}
                                valueLabelDisplay="auto"
                                disabled={updatingDashboardSettings}
                                sx={{
                                    color: '#1e3a5f',
                                    '& .MuiSlider-markLabel': {
                                        fontSize: '0.75rem'
                                    }
                                }}
                            />
                        </Box>

                        {/* Transition Type */}
                        <FormControl fullWidth>
                            <InputLabel>אנימציית מעבר</InputLabel>
                            <Select
                                value={dashboardTransition}
                                onChange={(e) => setDashboardTransition(e.target.value)}
                                label="אנימציית מעבר"
                                disabled={updatingDashboardSettings}
                            >
                                <MenuItem value="none">ללא אנימציה</MenuItem>
                                <MenuItem value="fade">Fade (דהייה)</MenuItem>
                                <MenuItem value="slide">Slide (החלקה)</MenuItem>
                                <MenuItem value="zoom">Zoom (זום)</MenuItem>
                                <MenuItem value="flip">Flip (היפוך)</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Info Box */}
                        <Box
                            sx={{
                                p: 2,
                                bgcolor: '#f0f7ff',
                                borderRadius: 2,
                                border: '1px solid #d0e7ff'
                            }}
                        >
                            <Typography variant="caption" sx={{ color: '#1e3a5f', display: 'block', mb: 0.5 }}>
                                💡 טיפ
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#666' }}>
                                האנימציה תופעל בעת המעבר מהלוח הקודם ללוח זה
                            </Typography>
                        </Box>
                    </Box>
                </ConfigDialog>

                {/* Delete Dashboard Confirmation Dialog */}
                <CenterDialog
                    open={deleteDashboardDialogOpen}
                    onClose={handleDeleteDashboardCancel}
                    onConfirm={handleDeleteDashboardConfirm}
                    title="אישור מחיקת לוח"
                    confirmText="מחק"
                    cancelText="ביטול"
                    confirmButtonColor="#dc2626"
                    isLoading={deleting}
                    minWidth="350px"
                    maxWidth="450px"
                >
                    <Typography sx={{ color: '#475569', fontSize: '0.95rem' }}>
                        האם אתה בטוח שברצונך למחוק את הלוח?
                    </Typography>
                    <Typography sx={{ color: '#dc2626', fontSize: '0.85rem', mt: 1, fontWeight: 500 }}>
                        פעולה זו תמחק את הלוח לצמיתות ולא ניתן לשחזר אותו.
                    </Typography>
                </CenterDialog>

                {/* Delete Playlist Confirmation Dialog */}
                <CenterDialog
                    open={deletePlaylistDialogOpen}
                    onClose={handleDeletePlaylistCancel}
                    onConfirm={handleDeletePlaylistConfirm}
                    title="אישור מחיקת מערך תצוגה"
                    confirmText="מחק"
                    cancelText="ביטול"
                    confirmButtonColor="#dc2626"
                    isLoading={deleting}
                    minWidth="350px"
                    maxWidth="450px"
                >
                    <Typography sx={{ color: '#475569', fontSize: '0.95rem' }}>
                        האם אתה בטוח שברצונך למחוק את מערך התצוגה?
                    </Typography>
                    <Typography sx={{ color: '#dc2626', fontSize: '0.85rem', mt: 1, fontWeight: 500 }}>
                        פעולה זו אינה ניתנת לביטול.
                    </Typography>
                </CenterDialog>

                {/* Schedule Calendar Dialog */}
                <ScheduleCalendarDialog
                    open={scheduleDialogOpen}
                    onClose={() => setScheduleDialogOpen(false)}
                    schedule={currentScreen.schedule || []}
                    playlists={playlistsWithDashboards.map(playlist => {
                        // חישוב isPublished בזמן אמת מה-dashboards הנוכחיים ב-Redux
                        const playlistDashboards = (playlist.items || []).map(item => {
                            return dashboards.find(d => d.dashboardId === item.dashboardId);
                        }).filter(d => d !== null && d !== undefined);

                        return {
                            ...playlist,
                            // פלייליסט זמין רק אם יש לו דשבורד מפורסם
                            // סכימה חדשה: latestVersionId על המסמך; ישנה: מערך versions
                            isPublished: playlistDashboards.some(dashboard =>
                                dashboard.latestVersionId || (dashboard.versions?.length > 0)
                            )
                        };
                    })}
                    onSave={handleSaveSchedule}
                />

                {/* Add Existing Dashboard Dialog - משמש גם להוספת לוח לפלייליסט קיים וגם לפלייליסט חדש */}
                <CenterDialog
                    open={addExistingDashboardDialogOpen}
                    onClose={() => {
                        if (!addingExistingDashboard) {
                            setAddExistingDashboardDialogOpen(false);
                            setIsCreatingNewPlaylist(false);
                        }
                    }}
                    onConfirm={handleConfirmAddExistingDashboard}
                    title={isCreatingNewPlaylist ? "בחר תבנית ללוח הראשון" : "הוסף לוח חדש"}
                    confirmText={addingExistingDashboard ? (isCreatingNewPlaylist ? 'יוצר...' : 'מוסיף...') : (isCreatingNewPlaylist ? 'צור לוח' : 'הוסף')}
                    cancelText="ביטול"
                    isLoading={addingExistingDashboard}
                    confirmDisabled={!selectedExistingDashboardId}
                    minWidth="1100px"
                    maxWidth="1440px"
                    blueHeader={true}
                    showCloseIcon={true}
                    textAlign="right"
                >
                    <Box sx={{ height: '72vh', minHeight: 0 }}>
                        <DashboardTemplateSelector
                            templates={predefinedTemplates}
                            existingDashboards={activeDashboards}
                            selectedTemplateId={selectedExistingDashboardId}
                            onSelectTemplate={handleSelectExistingDashboard}
                            organization={organization}
                            catalog={catalog}
                            dispatch={dispatch}
                            loading={false}
                        />
                    </Box>
                </CenterDialog>
            </Box>
        );
    }

    // List View - Screens List
    return (
        <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Header - Fixed */}
            <Box sx={{
                p: 4, pb: 2, flexShrink: 0,
                background: 'linear-gradient(135deg, rgba(30,58,95,0.04) 0%, rgba(255,255,255,0) 70%)',
                borderBottom: '1px solid rgba(30,58,95,0.09)',
                mb: 3
            }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e3a5f', mb: 0.5, letterSpacing: '-0.01em' }}>
                    מסכי תצוגה
                </Typography>
                <Typography variant="body1" sx={{ color: '#5a6978' }}>
                    {organization?.name || 'הארגון שלך'} - ניהול מסכים
                </Typography>
            </Box>

            {/* Screens Grid - Scrollable */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 4, pb: 4 }}>
                {suspendedScreenIds.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>
                        יש לכם {screens.length} מסכים — מגבלת התוכנית הנוכחית היא {screensLimit}.
                        {' '}{suspendedScreenIds.length} מסכים מושהים ואינם מציגים תוכן.
                        לשדרוג פנו אלינו.
                    </Alert>
                )}
                {screens.length === 0 ? (
                    <Box
                        sx={{
                            textAlign: 'center',
                            py: 8,
                            bgcolor: 'white',
                            borderRadius: '12px',
                            border: '2px dashed rgba(30,58,95,0.18)'
                        }}
                    >
                        <TvIcon sx={{ fontSize: 80, color: '#ccc', mb: 2 }} />
                        <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
                            אין מסכים עדיין
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#999', mb: 3 }}>
                            צור את המסך הראשון שלך כדי להתחיל
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddScreenClick}
                            sx={{
                                bgcolor: '#1e3a5f',
                                boxShadow: '0 2px 10px rgba(30,58,95,0.25)',
                                borderRadius: '8px',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    bgcolor: '#152a47',
                                    boxShadow: '0 4px 18px rgba(30,58,95,0.38)',
                                    transform: 'translateY(-1px)'
                                }
                            }}
                        >
                            צור מסך חדש
                        </Button>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: 3
                        }}
                    >
                        {screens
                            .sort((a, b) => {
                                // מיון לפי תאריך יצירה - הישן ביותר ראשון
                                const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
                                const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
                                return dateA - dateB;
                            })
                            .map((screen) => {
                                const isSuspended = suspendedScreenIds.includes(screen.screenId);
                                return (
                                <Card
                                    key={screen.screenId}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        borderRadius: '12px',
                                        transition: 'all 0.3s',
                                        opacity: isSuspended ? 0.7 : 1,
                                        '&:hover': isSuspended ? {} : {
                                            transform: 'translateY(-4px)',
                                            boxShadow: '0 12px 32px rgba(30,58,95,0.14), 0 2px 8px rgba(30,58,95,0.08)'
                                        }
                                    }}
                                >
                                    <CardActionArea
                                        onClick={() => !isSuspended && handleScreenClick(screen.screenId)}
                                        disabled={isSuspended}
                                        sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                                    >
                                        {(() => {
                                            const screenPlaylists = playlists.filter(p => p.screenId === screen.screenId);
                                            const hasPublished = screenPlaylists.some(playlist =>
                                                (playlist.items || []).some(item => {
                                                    const dash = dashboards?.find(d => d.dashboardId === item.dashboardId);
                                                    return !!(dash?.latestVersionId || dash?.versions?.length > 0);
                                                })
                                            );
                                            return (
                                                <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16/9', overflow: 'hidden' }}>
                                                    {isSuspended ? (
                                                        <Box sx={{ width: '100%', height: '100%', bgcolor: '#f1f5f9', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                                            <TvIcon sx={{ fontSize: 48, color: '#94a3b8' }} />
                                                            <Chip label="מושהה" size="small" sx={{ bgcolor: '#ef4444', color: '#fff', fontWeight: 700, fontSize: '0.7rem' }} />
                                                        </Box>
                                                    ) : hasPublished ? (
                                                        <iframe
                                                            src={`${window.location.origin}/${organization?.orgHash}/${screen.url}`}
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                border: 'none',
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                pointerEvents: 'none'
                                                            }}
                                                            title={screen.name}
                                                        />
                                                    ) : (
                                                        <Box sx={{ width: '100%', height: '100%', bgcolor: '#eef2f7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <TvIcon sx={{ fontSize: 48, color: '#1e3a5f', opacity: 0.2 }} />
                                                        </Box>
                                                    )}
                                                </Box>
                                            );
                                        })()}

                                        <Box sx={{ px: 1.5, height: 36, display: 'flex', alignItems: 'center', gap: 0.75, overflow: 'hidden' }}>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: isSuspended ? '#94a3b8' : '#1e3a5f', whiteSpace: 'nowrap' }}>
                                                {screen.name}
                                            </Typography>
                                            {screen.description && !isSuspended && (
                                                <>
                                                    <Typography variant="caption" sx={{ color: '#ccc' }}>•</Typography>
                                                    <Typography variant="caption" sx={{ color: '#999', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {screen.description}
                                                    </Typography>
                                                </>
                                            )}
                                        </Box>
                                    </CardActionArea>

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 1,
                                            p: 1,
                                            borderTop: '1px solid rgba(30,58,95,0.08)'
                                        }}
                                    >
                                        <Chip
                                            label={`${playlists.filter(p => p.screenId === screen.screenId).length} מערכי תצוגה`}
                                            size="small"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: '0.7rem',
                                                bgcolor: 'rgba(30,58,95,0.07)',
                                                border: '1px solid rgba(30,58,95,0.14)',
                                                color: '#1e3a5f',
                                            }}
                                        />
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        <Tooltip title="עריכת מסך">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleScreenClick(screen.screenId);
                                                }}
                                                sx={{
                                                    color: '#1e3a5f',
                                                    '&:hover': {
                                                        bgcolor: 'rgba(30, 58, 95, 0.08)'
                                                    }
                                                }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="תצוגה מקדימה">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => handlePreviewScreen(screen, e)}
                                                sx={{
                                                    color: '#1e3a5f',
                                                    '&:hover': {
                                                        bgcolor: 'rgba(30, 58, 95, 0.08)'
                                                    }
                                                }}
                                            >
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="הגדרות מסך">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => handleEditScreen(screen, e)}
                                                sx={{ color: '#1e3a5f' }}
                                            >
                                                <SettingsIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="מחק מסך">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => handleDeleteScreen(screen.screenId, e)}
                                                sx={{ color: '#d32f2f' }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        </Box>
                                    </Box>
                                </Card>
                                );
                            })}

                        {/* כרטיס הוספת מסך - בסוף הגריד */}
                        <Card
                            onClick={handleAddScreenClick}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                borderRadius: '12px',
                                border: '2px dashed rgba(30,58,95,0.3)',
                                bgcolor: 'rgba(30,58,95,0.02)',
                                cursor: 'pointer',
                                transition: 'all 0.25s',
                                '&:hover': {
                                    border: '2px dashed rgba(30,58,95,0.6)',
                                    bgcolor: 'rgba(30,58,95,0.05)',
                                    boxShadow: '0 6px 20px rgba(30,58,95,0.12)',
                                    transform: 'translateY(-4px)',
                                }
                            }}
                        >
                            {/* אזור תצוגה מקדימה */}
                            <Box sx={{ width: '100%', aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#eef2f7' }}>
                                <AddIcon sx={{ fontSize: 48, color: '#1e3a5f' }} />
                            </Box>
                            <Box sx={{ px: 1.5, height: 36, display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1e3a5f' }}>
                                    הוסף מסך חדש
                                </Typography>
                            </Box>
                            {/* שורת כפתורים ריקה - תואמת לגובה של כרטיס רגיל */}
                            <Box sx={{ minHeight: 40 }} />
                        </Card>
                    </Box>
                )}
            </Box>


            {/* Create Screen Dialog */}
            <ConfigDialog
                open={createDialogOpen}
                onClose={() => {
                    if (!creating) {
                        setCreateDialogOpen(false);
                        setNewScreenName('');
                        setNewScreenDescription('');
                        setNewScreenUrl('');
                        setUrlError('');
                    }
                }}
                onConfirm={handleNextCreateScreen}
                title="צור מסך חדש"
                confirmText="הבא"
                cancelText="ביטול"
                isLoading={false}
                confirmDisabled={!newScreenName.trim() || !newScreenUrl.trim() || !!urlError}
                width={600}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                        autoFocus
                        label="שם המסך"
                        fullWidth
                        value={newScreenName}
                        onChange={(e) => handleNewScreenNameChange(e.target.value)}
                        disabled={creating}
                        required
                    />
                    <TextField
                        label="תיאור (אופציונלי)"
                        fullWidth
                        multiline
                        rows={2}
                        value={newScreenDescription}
                        onChange={(e) => setNewScreenDescription(e.target.value)}
                        disabled={creating}
                    />

                    {/* URL Section */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            bgcolor: '#f8fafc',
                            border: `1px solid ${urlError ? '#ef4444' : '#e2e8f0'}`,
                            borderRadius: 1
                        }}
                    >
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 1 }}>
                            קישור המסך
                        </Typography>
                        <TextField
                            fullWidth
                            value={newScreenUrl}
                            onChange={(e) => handleNewScreenUrlChange(e.target.value)}
                            disabled={creating}
                            error={!!urlError}
                            helperText={urlError || 'ניתן לערוך רק את שם המסך'}
                            placeholder="screen-name"
                            InputProps={{
                                startAdornment: (
                                    <Box
                                        component="span"
                                        sx={{
                                            color: '#64748b',
                                            fontSize: '0.875rem',
                                            whiteSpace: 'nowrap',
                                            mr: 0.5
                                        }}
                                    >
                                        {window.location.origin}/{organization?.orgHash || generateOrgHash(organizationId)}/
                                    </Box>
                                )
                            }}
                            sx={{
                                '& .MuiInputBase-root': {
                                    fontSize: '0.875rem',
                                    direction: 'ltr',
                                    textAlign: 'left'
                                },
                                '& .MuiInputBase-input': {
                                    direction: 'ltr',
                                    textAlign: 'left'
                                }
                            }}
                        />
                        {newScreenUrl && !urlError && (
                            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: '#f0fdf4', borderRadius: '4px' }}>
                                <Typography variant="caption" sx={{ color: '#10b981', flex: 1, wordBreak: 'break-all', direction: 'ltr', textAlign: 'left' }}>
                                    ✓ {window.location.origin}/{organization?.orgHash || generateOrgHash(organizationId)}/{newScreenUrl}
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        const orgHash = organization?.orgHash || generateOrgHash(organizationId);
                                        navigator.clipboard.writeText(`${window.location.origin}/${orgHash}/${newScreenUrl}`);
                                        setCopySuccess(true);
                                        setTimeout(() => setCopySuccess(false), 2000);
                                    }}
                                    sx={{ color: '#10b981' }}
                                >
                                    {copySuccess ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                </IconButton>
                            </Box>
                        )}
                    </Paper>
                </Box>
            </ConfigDialog>

            {/* New Screen - Template Selector Dialog */}
            <CenterDialog
                open={newScreenTemplateSelectorOpen}
                onClose={() => {
                    if (!creating) {
                        setNewScreenTemplateSelectorOpen(false);
                        setNewScreenSelectedTemplateId(null);
                    }
                }}
                onConfirm={handleCreateScreenWithTemplate}
                title="בחר תבנית ללוח"
                confirmText={creating ? 'יוצר...' : 'צור מסך'}
                cancelText="חזרה"
                onCancel={handleBackToScreenCreate}
                isLoading={creating}
                confirmDisabled={!newScreenSelectedTemplateId || creating}
                minWidth="1100px"
                maxWidth="1440px"
                blueHeader={true}
                showCloseIcon={true}
                textAlign="right"
            >
                <Box sx={{ height: '72vh', minHeight: 0 }}>
                    <DashboardTemplateSelector
                        templates={predefinedTemplates}
                        existingDashboards={activeDashboards}
                        selectedTemplateId={newScreenSelectedTemplateId}
                        onSelectTemplate={setNewScreenSelectedTemplateId}
                        organization={organization}
                        catalog={catalog}
                        dispatch={dispatch}
                        loading={false}
                    />
                </Box>
            </CenterDialog>

            {/* Edit Screen Dialog */}
            <ConfigDialog
                open={editDialogOpen}
                onClose={() => !updating && setEditDialogOpen(false)}
                onConfirm={handleUpdateScreen}
                title="ערוך מסך"
                confirmText={updating ? 'מעדכן...' : 'עדכן'}
                cancelText="ביטול"
                isLoading={updating}
                confirmDisabled={!editScreenName.trim() || !!urlError}
                width={500}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                        autoFocus
                        label="שם המסך"
                        fullWidth
                        value={editScreenName}
                        onChange={(e) => setEditScreenName(e.target.value)}
                        disabled={updating}
                        required
                    />
                    <TextField
                        label="תיאור (אופציונלי)"
                        fullWidth
                        multiline
                        rows={2}
                        value={editScreenDescription}
                        onChange={(e) => setEditScreenDescription(e.target.value)}
                        disabled={updating}
                    />

                    {/* URL Section - Read Only */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            bgcolor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: 1
                        }}
                    >
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 1 }}>
                            קישור המסך
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: 'white', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#1e293b',
                                    fontSize: '0.875rem',
                                    flex: 1,
                                    direction: 'ltr',
                                    textAlign: 'left',
                                    wordBreak: 'break-all'
                                }}
                            >
                                {window.location.origin}/{organization?.orgHash || generateOrgHash(organizationId)}/{editScreenUrl}
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={() => {
                                    const orgHash = organization?.orgHash || generateOrgHash(organizationId);
                                    navigator.clipboard.writeText(`${window.location.origin}/${orgHash}/${editScreenUrl}`);
                                    setCopySuccess(true);
                                    setTimeout(() => setCopySuccess(false), 2000);
                                }}
                                sx={{ color: '#1e3a5f' }}
                            >
                                {copySuccess ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                            </IconButton>
                        </Box>
                        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5 }}>
                            הקישור נוצר אוטומטית ולא ניתן לעריכה
                        </Typography>
                    </Paper>
                </Box>
            </ConfigDialog>

            {/* Delete Screen Confirmation Dialog */}
            <CenterDialog
                open={deleteScreenDialogOpen}
                onClose={handleDeleteScreenCancel}
                onConfirm={handleDeleteScreenConfirm}
                title="אישור מחיקת מסך"
                confirmText="מחק"
                cancelText="ביטול"
                confirmButtonColor="#dc2626"
                isLoading={deleting}
                minWidth="380px"
                maxWidth="480px"
            >
                <Typography sx={{ color: '#475569', fontSize: '0.95rem' }}>
                    האם אתה בטוח שברצונך למחוק את המסך?
                </Typography>
                <Typography sx={{ color: '#dc2626', fontSize: '0.85rem', mt: 1, fontWeight: 500 }}>
                    פעולה זו תמחק גם את כל מערכי התצוגה שלו.
                </Typography>
                <Typography sx={{ color: '#dc2626', fontSize: '0.85rem', mt: 0.5, fontWeight: 600 }}>
                    פעולה זו אינה ניתנת לביטול.
                </Typography>
            </CenterDialog>

            {/* Loading Overlay */}
            <LoadingOverlay
                open={screensLoading || creating || updating || updatingPlaylist || creatingPlaylist || loadingDashboards}
            />

            {/* Toast for dashboard toggle feedback */}
            <Toast
                open={toastOpen}
                message={toastMessage}
                severity="info"
                onClose={() => setToastOpen(false)}
                autoHideDuration={3000}
            />
        </Box >
    );
};

export default Screen;
